/**
 * ReaderTraining.jsx — comprehensive Reading Training program.
 *
 * Feb 9 2026 user spec: *"Build a reading training with flash and chunk
 * reading as on demo in journey to top reader. Assign xp for each
 * successful reading and more for comprehension and deduction for non
 * retention 10xp for each reading passage 10 xp per correct answer and
 * retention and -5xp for failed answer/non retention. Make it a
 * comprehensive training and measure increase in reading speed and
 * comprehension and gamify it in levels using the same ones on the
 * reading levels and evolution."*
 *
 * Differences vs the public `/quiz-demo`:
 *   • Requires login (XP is real here, not a teaser).
 *   • Persists session state on the backend (`/api/reader-training/session/*`).
 *   • Shows a running XP HUD with +10 / -5 delta tickers per passage.
 *   • Shows the user's current level + tier and progress-to-next-level.
 *   • End screen shows IMPROVEMENT vs the user's previous best (Δ wpm, Δ retention).
 *   • Progress sparklines from past sessions.
 *
 * Reuses the visual language from QuizDemo (black HUD + red accent + serif),
 * but with a calmer indigo overlay so the training feels distinct from the
 * adrenaline-pumping public Speed Reader demo.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Trophy, ChevronRight, ArrowLeft, RotateCw, Brain, TrendingUp,
  Repeat, Pause, Zap, Crown, Flame, ArrowUp, ArrowDown, Coins,
} from 'lucide-react';
import { authFetch, checkAuth } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Stage = {
  INTRO:     'intro',
  COUNTDOWN: 'countdown',
  FLASH:     'flash',
  QUESTION:  'question',
  FEEDBACK:  'feedback',   // shows +/-XP tickers between passages
  SUMMARY:   'summary',
};

const WPM_TIERS = [
  100, 200, 300, 400, 500, 600, 700, 800, 900, 1000,
];
const CHUNK_MODES = [
  { id: 1, label: 'Single word' },
  { id: 2, label: 'Two-word chunks' },
  { id: 3, label: 'Three-word chunks' },
];

// ── Sparkline helper ─────────────────────────────────────────────
// Tiny inline SVG sparkline. Takes an array of numbers + max → renders.
const Sparkline = ({ values, max, color = '#22d3ee', height = 36, width = 220 }) => {
  if (!values?.length) return <div className="text-xs text-white/30">No data yet</div>;
  const safeMax = max ?? Math.max(...values, 1);
  const pad = 4;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const stepX = values.length > 1 ? innerW / (values.length - 1) : 0;
  const pts = values.map((v, i) => {
    const x = pad + stepX * i;
    const y = pad + innerH - (Math.max(0, Math.min(safeMax, v)) / safeMax) * innerH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {values.length === 1 && (
        <circle cx={pad} cy={pad + innerH - (values[0] / safeMax) * innerH} r="3" fill={color} />
      )}
    </svg>
  );
};

const ReaderTraining = () => {
  const navigate = useNavigate();

  // ── Setup state ──────────────────────────────────────────────
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);
  const [progress, setProgress] = useState(null);   // { sessions, baseline, level }
  const [wpm, setWpm] = useState(300);
  const [chunkSize, setChunkSize] = useState(1);
  const [autoCycle, setAutoCycle] = useState(false);
  const autoCycleRef = useRef(autoCycle);
  useEffect(() => { autoCycleRef.current = autoCycle; }, [autoCycle]);
  const chunkSizeRef = useRef(chunkSize);
  useEffect(() => { chunkSizeRef.current = chunkSize; }, [chunkSize]);

  // ── Run state ────────────────────────────────────────────────
  const [stage, setStage] = useState(Stage.INTRO);
  const [session, setSession] = useState(null);     // { session_id, passages, baseline, level }
  const [passageIdx, setPassageIdx] = useState(0);
  const [flashIdx, setFlashIdx] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [pickedIdx, setPickedIdx] = useState(null);
  const [lastResp, setLastResp] = useState(null);   // server response for passage
  const [runningXP, setRunningXP] = useState(0);
  const [summary, setSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cyclePending, setCyclePending] = useState(0);

  // ── Boot — auth + config + progress ─────────────────────────
  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      setUser(u);
      const [c, p] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/api/reader-training/config`).then((r) => r.json()),
        authFetch(`${BACKEND_URL}/api/reader-training/progress?limit=10`).then((r) => r.json()),
      ]);
      if (c.status === 'fulfilled') setConfig(c.value);
      if (p.status === 'fulfilled') setProgress(p.value);
    })();
  }, [navigate]);

  // ── Start a new session ─────────────────────────────────────
  const startTraining = async () => {
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/reader-training/session/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wpm, chunk_size: chunkSize }),
      });
      const d = await r.json();
      setSession(d);
      setPassageIdx(0);
      setFlashIdx(0);
      setRunningXP(0);
      setLastResp(null);
      setPickedIdx(null);
      setSummary(null);
      setCountdown(3);
      setStage(Stage.COUNTDOWN);
    } catch {
      // soft-fail: stay on intro
    } finally {
      setSubmitting(false);
    }
  };

  // ── Countdown ───────────────────────────────────────────────
  useEffect(() => {
    if (stage !== Stage.COUNTDOWN) return;
    if (countdown <= 0) { setStage(Stage.FLASH); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 700);
    return () => clearTimeout(t);
  }, [stage, countdown]);

  // ── Current passage / chunks ────────────────────────────────
  const currentPassage = session?.passages?.[passageIdx] || null;
  const words = useMemo(
    () => (currentPassage?.passage || '').split(/\s+/).filter(Boolean),
    [currentPassage]
  );
  const chunks = useMemo(() => {
    if (!words.length) return [];
    const out = [];
    for (let i = 0; i < words.length; i += chunkSize) {
      out.push(words.slice(i, i + chunkSize).join(' '));
    }
    return out;
  }, [words, chunkSize]);

  // ── Flash effect ───────────────────────────────────────────
  useEffect(() => {
    if (stage !== Stage.FLASH) return;
    if (!chunks.length) { setStage(Stage.QUESTION); return; }
    if (flashIdx >= chunks.length) { setStage(Stage.QUESTION); return; }
    const msPerChunk = Math.max(80, Math.round((60_000 / wpm) * chunkSize));
    const t = setTimeout(() => setFlashIdx((i) => i + 1), msPerChunk);
    return () => clearTimeout(t);
  }, [stage, flashIdx, chunks, wpm, chunkSize]);

  // ── Submit an answer ───────────────────────────────────────
  const onPick = async (idx) => {
    if (pickedIdx !== null || !session) return;
    setPickedIdx(idx);
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/reader-training/session/passage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.session_id,
          passage_id: currentPassage.id,
          choice: idx,
          read_completed: true,
        }),
      });
      const d = await r.json();
      setLastResp(d);
      setRunningXP((d?.running?.total_xp_delta) ?? runningXP);
      setStage(Stage.FEEDBACK);
    } catch {
      setLastResp({ is_correct: false, delta_total_xp: 0, delta_velocity_xp: 0, delta_comprehension_xp: 0 });
      setStage(Stage.FEEDBACK);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Feedback dwell → advance ───────────────────────────────
  useEffect(() => {
    if (stage !== Stage.FEEDBACK) return;
    const t = setTimeout(() => {
      setPickedIdx(null);
      setLastResp(null);
      const nextIdx = passageIdx + 1;
      if (nextIdx >= (session?.passages?.length || 0)) {
        endTraining();
      } else {
        setPassageIdx(nextIdx);
        setFlashIdx(0);
        setCountdown(2);
        setStage(Stage.COUNTDOWN);
      }
    }, 1600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // ── End / summary ──────────────────────────────────────────
  const endTraining = async () => {
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/reader-training/session/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: session.session_id }),
      });
      const d = await r.json();
      setSummary(d);
      // Refresh progress so the sparklines reflect this session.
      authFetch(`${BACKEND_URL}/api/reader-training/progress?limit=10`)
        .then((r) => r.json())
        .then(setProgress)
        .catch(() => {});
    } catch {
      // fallback minimal summary
      setSummary({
        wpm, chunk_size: chunkSize,
        passages_read: passageIdx + 1,
        correct_answers: 0,
        wrong_answers: passageIdx + 1,
        retention_pct: 0,
        total_xp_delta: runningXP,
        improvement: { wpm_delta: 0, retention_delta: 0, is_new_best_wpm: false, is_new_best_ret: false, sessions_completed: 0 },
        level_after: { level: 1, tier: 'Novice Explorer' },
      });
    } finally {
      setSubmitting(false);
      setStage(Stage.SUMMARY);
    }
  };

  // ── Auto-cycle on summary screen ───────────────────────────
  useEffect(() => {
    if (stage !== Stage.SUMMARY) { setCyclePending(0); return; }
    if (!autoCycleRef.current) { setCyclePending(0); return; }
    let remaining = 5;
    setCyclePending(remaining);
    const startDelay = setTimeout(() => {
      const interval = setInterval(() => {
        remaining -= 1;
        setCyclePending(remaining);
        if (remaining <= 0) {
          clearInterval(interval);
          if (autoCycleRef.current) {
            const next = (chunkSizeRef.current % 3) + 1;
            setChunkSize(next);
            setTimeout(() => { if (autoCycleRef.current) startTraining(); }, 50);
          }
        }
      }, 1000);
      // eslint-disable-next-line no-param-reassign
      startDelay._interval = interval;
    }, 2000);
    return () => {
      clearTimeout(startDelay);
      if (startDelay._interval) clearInterval(startDelay._interval);
      setCyclePending(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, summary]);

  const stopCycle = () => { setAutoCycle(false); setCyclePending(0); };

  // ───────────────────────────────────────────────────────────
  // Derived UI values
  const level = progress?.level || session?.level;
  const baseline = progress?.baseline || session?.baseline;
  const past = progress?.sessions || [];
  const wpmHistory = past.map((s) => s.wpm).filter(Boolean);
  const retentionHistory = past.map((s) => s.retention_pct || 0);

  // ───────────────────────────────────────────────────────────
  return (
    <main
      className="min-h-screen w-full bg-[#0B0C10] text-[#EAE6DD] flex flex-col items-stretch overflow-x-hidden relative"
      style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
      data-testid="reader-training-root"
    >
      {/* HUD */}
      <div className="px-5 py-3 flex items-center justify-between border-b border-white/5">
        <button
          type="button"
          onClick={() => navigate('/reader-journey')}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white"
          data-testid="reader-training-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Journey
        </button>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Reading Training</span>
        {level ? (
          <div className="text-right" data-testid="reader-training-level-badge">
            <p className="text-[9px] uppercase tracking-widest text-amber-300/70">{level.tier}</p>
            <p className="text-xs font-black text-amber-200 tabular-nums">Lv {level.level}</p>
          </div>
        ) : <span className="w-12" />}
      </div>

      {/* Stage: INTRO */}
      {stage === Stage.INTRO && (
        <div className="flex-1 px-4 sm:px-6 py-8 max-w-3xl mx-auto w-full space-y-6" data-testid="reader-training-intro">
          {/* Header */}
          <div>
            <p className="text-emerald-300 font-black text-xs uppercase tracking-[0.4em] mb-2">Training Program</p>
            <h1
              className="text-4xl sm:text-5xl font-black leading-[1.05] text-white"
              style={{ fontFamily: '"Fraunces", "Playfair Display", Georgia, serif' }}
            >
              Train your reading.{' '}
              <span className="text-emerald-300">Earn real XP.</span>
            </h1>
            <p className="text-sm text-white/60 mt-3 max-w-xl">
              5 flash-read passages with comprehension checks. Every passage builds
              your <strong className="text-white">reading velocity</strong>; every correct
              answer builds your <strong className="text-white">comprehension</strong>.
            </p>
          </div>

          {/* XP rules card */}
          {config && (
            <section
              className="rounded-2xl border border-emerald-400/30 bg-emerald-900/10 p-4 grid grid-cols-3 gap-2"
              data-testid="reader-training-xp-rules"
            >
              <div className="text-center">
                <p className="text-[9px] uppercase tracking-widest text-white/40">Per passage</p>
                <p className="text-2xl font-black text-emerald-300 tabular-nums">
                  +{config.xp_rules.per_passage_read}
                </p>
                <p className="text-[10px] text-white/50">XP velocity</p>
              </div>
              <div className="text-center border-x border-white/5">
                <p className="text-[9px] uppercase tracking-widest text-white/40">Correct answer</p>
                <p className="text-2xl font-black text-cyan-300 tabular-nums">
                  +{config.xp_rules.per_correct_answer}
                </p>
                <p className="text-[10px] text-white/50">XP comprehension</p>
              </div>
              <div className="text-center">
                <p className="text-[9px] uppercase tracking-widest text-white/40">Wrong / missed</p>
                <p className="text-2xl font-black text-rose-400 tabular-nums">
                  {config.xp_rules.per_wrong_answer}
                </p>
                <p className="text-[10px] text-white/50">XP penalty</p>
              </div>
            </section>
          )}

          {/* Level + baseline */}
          {level && (
            <section
              className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-900/20 to-slate-900/40 p-4"
              data-testid="reader-training-level-card"
            >
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-amber-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] uppercase tracking-widest text-amber-300/80">
                    Level {level.level} · {level.tier}
                  </p>
                  <div className="h-2 rounded-full bg-slate-900/60 overflow-hidden mt-1">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all"
                      style={{ width: `${Math.min(100, ((level.xp_total || 0) % level.xp_for_next) / level.xp_for_next * 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-white/50 mt-1 tabular-nums">
                    {(level.xp_total || 0).toLocaleString()} / {level.xp_for_next.toLocaleString()} XP to next level
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* Baseline + Progress sparklines */}
          {baseline && (
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="reader-training-progress-charts">
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Reading speed (wpm)</p>
                  <p className="text-sm font-black text-cyan-300 tabular-nums">
                    {baseline.best_wpm || 0}
                    <span className="text-[10px] text-white/40 ml-1">best</span>
                  </p>
                </div>
                <Sparkline values={wpmHistory} max={1000} color="#22d3ee" />
                <p className="text-[10px] text-white/50 mt-1">
                  {baseline.sessions_completed} session{baseline.sessions_completed === 1 ? '' : 's'} · avg {baseline.average_wpm || 0} wpm
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Retention %</p>
                  <p className="text-sm font-black text-emerald-300 tabular-nums">
                    {baseline.best_retention_pct || 0}<span className="text-[10px] text-white/40 ml-1">%</span>
                  </p>
                </div>
                <Sparkline values={retentionHistory} max={100} color="#34d399" />
                <p className="text-[10px] text-white/50 mt-1">
                  avg {baseline.average_retention_pct || 0}% across {baseline.sessions_completed} session{baseline.sessions_completed === 1 ? '' : 's'}
                </p>
              </div>
            </section>
          )}

          {/* WPM tier picker */}
          <section data-testid="reader-training-wpm-picker">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Reading speed target</p>
            <div className="grid grid-cols-5 gap-1.5">
              {WPM_TIERS.map((t) => {
                const active = wpm === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setWpm(t)}
                    className={`text-center rounded-xl border px-1 py-2 transition ${
                      active
                        ? 'border-emerald-400 bg-emerald-400/10'
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                    data-testid={`reader-training-wpm-${t}`}
                  >
                    <span className="text-sm font-black tabular-nums">{t}</span>
                    <span className="block text-[9px] uppercase tracking-widest text-white/40">wpm</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Chunk size picker */}
          <section data-testid="reader-training-chunk-picker">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Chunk size</p>
            <div className="grid grid-cols-3 gap-2">
              {CHUNK_MODES.map((c) => {
                const active = chunkSize === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setChunkSize(c.id)}
                    className={`text-left rounded-xl border px-3 py-2 transition ${
                      active
                        ? 'border-amber-400 bg-amber-400/10'
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                    data-testid={`reader-training-chunk-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-base font-black">
                        {c.id}<span className="text-[9px] uppercase tracking-widest text-white/40 ml-1">w</span>
                      </span>
                    </div>
                    <p className="text-[10px] text-white/50 leading-tight mt-0.5 line-clamp-1">{c.label}</p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Auto-cycle */}
          <label
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${
              autoCycle ? 'border-amber-400 bg-amber-400/10' : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
            }`}
            data-testid="reader-training-auto-cycle-toggle"
          >
            <input
              type="checkbox"
              checked={autoCycle}
              onChange={(e) => setAutoCycle(e.target.checked)}
              className="mt-1 w-4 h-4 accent-amber-500"
              data-testid="reader-training-auto-cycle-input"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white flex items-center gap-1">
                <Repeat className="w-3.5 h-3.5 text-amber-300" />
                Continuous training (auto-shift chunks)
              </p>
              <p className="text-[10px] text-white/55 mt-0.5">
                Sessions auto-restart with the next chunk size (1→2→3) until you stop.
              </p>
            </div>
          </label>

          <button
            onClick={startTraining}
            disabled={submitting}
            className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white font-black px-8 py-4 rounded-full uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 shadow-[0_0_40px_rgba(16,185,129,0.4)]"
            data-testid="reader-training-start-btn"
          >
            {submitting ? 'Loading…' : 'Begin training session'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* COUNTDOWN */}
      {stage === Stage.COUNTDOWN && (
        <div className="flex-1 flex flex-col items-center justify-center" data-testid="reader-training-countdown">
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-3">
            Passage {passageIdx + 1} of {session?.passages?.length || 5}
          </p>
          <div
            className="text-emerald-300 font-black text-9xl sm:text-[12rem] tabular-nums animate-pulse"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            key={countdown}
          >
            {countdown > 0 ? countdown : 'GO'}
          </div>
          <div className="absolute top-16 right-5 text-right" data-testid="reader-training-xp-hud">
            <p className="text-[9px] uppercase tracking-widest text-white/40">Session XP</p>
            <p className="text-2xl font-black text-amber-300 tabular-nums">{runningXP >= 0 ? '+' : ''}{runningXP}</p>
          </div>
        </div>
      )}

      {/* FLASH */}
      {stage === Stage.FLASH && currentPassage && (
        <div className="flex-1 flex flex-col" data-testid="reader-training-flash">
          <div className="h-1.5 w-full bg-white/5">
            <div
              className="h-full bg-emerald-400 transition-[width] duration-100"
              style={{ width: `${(flashIdx / Math.max(1, chunks.length)) * 100}%` }}
            />
          </div>
          <div className="flex-1 flex items-center justify-center px-4">
            <span
              className={`font-black text-white text-center break-words ${
                chunkSize === 1 ? 'text-5xl sm:text-7xl md:text-8xl' :
                chunkSize === 2 ? 'text-4xl sm:text-6xl md:text-7xl' :
                                   'text-3xl sm:text-5xl md:text-6xl'
              }`}
              style={{ fontFamily: '"Fraunces", Georgia, serif', letterSpacing: '-0.02em' }}
              data-testid="reader-training-current-word"
            >
              {chunks[flashIdx] || ''}
            </span>
          </div>
          <div className="px-5 py-4 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-white/40">
              Passage {passageIdx + 1} / {session?.passages?.length || 5} · {chunkSize}w chunks
            </span>
            <div className="font-mono text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Reading at</div>
              <div className="text-3xl font-black text-amber-300 tabular-nums">{wpm}</div>
            </div>
          </div>
        </div>
      )}

      {/* QUESTION */}
      {stage === Stage.QUESTION && currentPassage && (
        <div className="flex-1 flex flex-col items-stretch justify-center px-5 py-8 gap-3 max-w-2xl mx-auto w-full" data-testid="reader-training-question">
          <div className="text-center mb-1">
            <p className="text-[10px] uppercase tracking-widest text-white/40">
              Retention check — passage {passageIdx + 1} of {session?.passages?.length || 5}
            </p>
            <h2 className="text-xl sm:text-2xl font-black text-white mt-1 leading-snug max-w-2xl mx-auto">
              {currentPassage.prompt}
            </h2>
          </div>
          {currentPassage.choices.map((choice, i) => {
            const picked = pickedIdx === i;
            let border = 'border-white/15 hover:border-white/40';
            if (pickedIdx !== null) {
              if (picked) border = 'border-emerald-400 bg-emerald-400/10';
              else border = 'border-white/10 opacity-60';
            }
            return (
              <button
                key={i}
                onClick={() => onPick(i)}
                disabled={pickedIdx !== null || submitting}
                className={`w-full text-left rounded-2xl border px-4 py-4 transition ${border}`}
                style={{ fontFamily: '"Fraunces", Georgia, serif' }}
                data-testid={`reader-training-choice-${i}`}
              >
                <span className="text-base sm:text-lg text-white">{choice}</span>
              </button>
            );
          })}
          <div className="text-center text-[10px] uppercase tracking-widest text-white/40 mt-2">
            Session XP so far:{' '}
            <strong className="text-amber-300 tabular-nums">{runningXP >= 0 ? '+' : ''}{runningXP}</strong>
          </div>
        </div>
      )}

      {/* FEEDBACK — show the XP delta animation for a moment */}
      {stage === Stage.FEEDBACK && lastResp && (
        <div className="flex-1 flex flex-col items-center justify-center gap-6" data-testid="reader-training-feedback">
          <p className={`text-[10px] uppercase tracking-[0.4em] font-black ${lastResp.is_correct ? 'text-emerald-300' : 'text-rose-400'}`}>
            {lastResp.is_correct ? 'Retained' : 'Missed'}
          </p>
          <div
            className={`text-7xl sm:text-8xl font-black tabular-nums ${
              lastResp.delta_total_xp > 0 ? 'text-emerald-300' : lastResp.delta_total_xp < 0 ? 'text-rose-400' : 'text-white/60'
            }`}
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            data-testid="reader-training-xp-delta"
          >
            {lastResp.delta_total_xp >= 0 ? '+' : ''}{lastResp.delta_total_xp}
            <span className="text-2xl text-white/40 ml-2">XP</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1 text-cyan-300">
              <Zap className="w-3.5 h-3.5" />
              +{lastResp.delta_velocity_xp} velocity
            </span>
            <span className={`inline-flex items-center gap-1 ${lastResp.delta_comprehension_xp >= 0 ? 'text-emerald-300' : 'text-rose-400'}`}>
              <Brain className="w-3.5 h-3.5" />
              {lastResp.delta_comprehension_xp >= 0 ? '+' : ''}{lastResp.delta_comprehension_xp} comprehension
            </span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">
            Session total: <strong className="text-amber-300 tabular-nums">{runningXP >= 0 ? '+' : ''}{runningXP}</strong>
          </div>
        </div>
      )}

      {/* SUMMARY */}
      {stage === Stage.SUMMARY && summary && (
        <div className="flex-1 px-4 sm:px-6 py-8 max-w-2xl mx-auto w-full space-y-5" data-testid="reader-training-summary">
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.4em] text-white/40">Session complete</p>
            <h2
              className="text-3xl sm:text-4xl font-black text-white mt-1"
              style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            >
              {summary.total_xp_delta >= 0 ? '+' : ''}{summary.total_xp_delta} XP earned
            </h2>
          </div>

          {/* Headline metrics */}
          <section className="grid grid-cols-3 gap-2" data-testid="reader-training-summary-metrics">
            <div className="rounded-2xl bg-cyan-900/20 border border-cyan-400/30 p-3 text-center">
              <p className="text-[9px] uppercase tracking-widest text-cyan-300/80">Speed</p>
              <p className="text-2xl font-black text-white tabular-nums">{summary.wpm}</p>
              <p className="text-[10px] text-white/50">wpm</p>
            </div>
            <div className="rounded-2xl bg-emerald-900/20 border border-emerald-400/30 p-3 text-center">
              <p className="text-[9px] uppercase tracking-widest text-emerald-300/80">Retention</p>
              <p className="text-2xl font-black text-white tabular-nums">{summary.retention_pct}%</p>
              <p className="text-[10px] text-white/50">{summary.correct_answers} / {summary.passages_read}</p>
            </div>
            <div className="rounded-2xl bg-amber-900/20 border border-amber-400/30 p-3 text-center">
              <p className="text-[9px] uppercase tracking-widest text-amber-300/80">Chunks</p>
              <p className="text-2xl font-black text-white tabular-nums">{summary.chunk_size}w</p>
              <p className="text-[10px] text-white/50">phrase mode</p>
            </div>
          </section>

          {/* Improvement vs baseline */}
          {summary.improvement && (
            <section
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
              data-testid="reader-training-improvement"
            >
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" /> Improvement vs your previous best
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  {summary.improvement.wpm_delta > 0
                    ? <ArrowUp className="w-4 h-4 text-emerald-300" />
                    : summary.improvement.wpm_delta < 0
                      ? <ArrowDown className="w-4 h-4 text-rose-400" />
                      : <span className="w-4 h-4 inline-block" />
                  }
                  <div>
                    <p className="text-sm font-black text-white tabular-nums">
                      {summary.improvement.wpm_delta > 0 ? '+' : ''}{summary.improvement.wpm_delta} wpm
                      {summary.improvement.is_new_best_wpm && <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-300">new best</span>}
                    </p>
                    <p className="text-[10px] text-white/50">speed</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {summary.improvement.retention_delta > 0
                    ? <ArrowUp className="w-4 h-4 text-emerald-300" />
                    : summary.improvement.retention_delta < 0
                      ? <ArrowDown className="w-4 h-4 text-rose-400" />
                      : <span className="w-4 h-4 inline-block" />
                  }
                  <div>
                    <p className="text-sm font-black text-white tabular-nums">
                      {summary.improvement.retention_delta > 0 ? '+' : ''}{summary.improvement.retention_delta}%
                      {summary.improvement.is_new_best_ret && <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-300">new best</span>}
                    </p>
                    <p className="text-[10px] text-white/50">retention</p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-white/50 mt-2 tabular-nums">
                {summary.improvement.sessions_completed} session{summary.improvement.sessions_completed === 1 ? '' : 's'} completed total
              </p>
            </section>
          )}

          {/* Level after */}
          {summary.level_after && (
            <section
              className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-900/30 to-slate-900/50 p-4 flex items-center gap-3"
              data-testid="reader-training-summary-level"
            >
              <Crown className="w-6 h-6 text-amber-300 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-amber-300/80">
                  Now: Level {summary.level_after.level} · {summary.level_after.tier}
                </p>
                <div className="h-2 rounded-full bg-slate-900/60 overflow-hidden mt-1">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all"
                    style={{ width: `${Math.min(100, ((summary.level_after.xp_total || 0) % (summary.level_after.xp_for_next || 1000)) / (summary.level_after.xp_for_next || 1000) * 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-white/50 mt-1 tabular-nums">
                  {(summary.level_after.xp_total || 0).toLocaleString()} / {(summary.level_after.xp_for_next || 0).toLocaleString()} XP to next
                </p>
              </div>
            </section>
          )}

          {/* Auto-cycle banner */}
          {autoCycle && cyclePending > 0 && (
            <div
              className="rounded-2xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 flex items-center gap-3"
              data-testid="reader-training-cycle-banner"
            >
              <Repeat className="w-5 h-5 text-amber-300 flex-shrink-0" />
              <p className="text-xs text-white/80 flex-1 min-w-0">
                Next session in{' '}
                <strong className="text-amber-200 tabular-nums">{cyclePending}s</strong> —{' '}
                <span className="text-white/70">{(chunkSize % 3) + 1}-word chunks</span>
              </p>
              <button
                type="button"
                onClick={stopCycle}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-[11px] font-bold uppercase tracking-widest"
                data-testid="reader-training-stop-cycle-btn"
              >
                <Pause className="w-3 h-3" /> Stop
              </button>
            </div>
          )}

          {/* CTAs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              onClick={startTraining}
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
              data-testid="reader-training-again-btn"
            >
              <RotateCw className="w-4 h-4" /> Train again
            </button>
            <button
              onClick={() => navigate('/reader-journey')}
              className="border border-white/20 text-white hover:bg-white/5 font-bold py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
              data-testid="reader-training-back-to-journey"
            >
              Back to Journey
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default ReaderTraining;
